import { WMSelectOption } from '@walkme/wm-ui';
import _isEquel from 'lodash/isEqual';
import { USER_IDENTIFIER_PROPERTY } from '../../../state/reducers/audiences';
import { IntegrationFieldTypes, IIntegrationPropertyValue } from '@walkme/audiences-types';
import {
    AudienceRuleGraphql,
    AudienceRuleOperatorEnum,
    IntegrationFieldRole,
    IntegrationGraphql,
    IntegrationPropertyValueGraphql,
    IntegrationTypeEnum
} from '../../../../generated/apollo-hooks';

export interface IRulesValue {
    name: string;
    amount: number;
}

export interface IRulesValuesDictionary {
    [integrationId: string]:
        | {
              [property: string]: WMSelectOption[];
          }
        | undefined;
}

export const ruleTemplate: AudienceRuleGraphql = {
    integrationId: '',
    property: '',
    alias: '',
    values: [],
    operator: AudienceRuleOperatorEnum.In
};

const THIRTY_DAYS_AGO = 30 * 24 * 3600 * 1000;

export const hasRulesChanged = (originalArray: AudienceRuleGraphql[], modifiedArray?: AudienceRuleGraphql[]): boolean => {
    const mapRules = (array: AudienceRuleGraphql[]) =>
        array.map((rule: AudienceRuleGraphql & { integrationType?: IntegrationTypeEnum; role?: IntegrationFieldRole }) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { __typename, integrationType, role, ...item } = rule;

            return {
                ...item,
                operator: item.operator || AudienceRuleOperatorEnum.In,
                values: item.values
                    .map((value: IntegrationPropertyValueGraphql | (IIntegrationPropertyValue & { __typename?: string })) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { amount, __typename, ...rest } = value;
                        return rest;
                    })
                    .sort((a: $TSFixMe, b: $TSFixMe) => (a.name.toLowerCase() > b.name.toLowerCase() ? -1 : 1))
            };
        });

    return !_isEquel(mapRules(originalArray), mapRules(modifiedArray || []));
};

export const validateRuleComplete = (rulesToSave: AudienceRuleGraphql[]) => {
    return rulesToSave.every(rule => rule?.property !== '' && rule?.values?.length !== 0);
};

export const createPropertyOptions = (
    rules: Array<
        Pick<AudienceRuleGraphql, 'property' | 'integrationId' | 'alias'> & {
            dataType?: string | null;
            operator?: AudienceRuleOperatorEnum | null;
            integrationName?: string;
        }
    >,
    rulesToSave?: AudienceRuleGraphql[]
) => {
    return rules.map(rule => {
        const isDisabled = rulesToSave
            ? Boolean(
                  rulesToSave.find(
                      ruleToSave =>
                          ruleToSave.property === rule.property &&
                          (ruleToSave.integrationId === rule.integrationId || ruleToSave.property === USER_IDENTIFIER_PROPERTY)
                  )
              )
            : false;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { integrationName, ...raw } = rule;
        return {
            raw: { ...raw },
            integrationId: rule.integrationId,
            integrationName: rule.integrationName,
            label: `${rule.alias || rule.property}`,
            value: rule.property,
            isDisabled,
            operator: rule.operator || AudienceRuleOperatorEnum.In
        };
    });
};

export const integrationsToPropertyOptions = (integrations: IntegrationGraphql[], rulesToSave?: AudienceRuleGraphql[]) => {
    if (!integrations) return [];

    const rules = integrations
        .map(integration =>
            integration?.fields
                .filter(
                    field =>
                        field.role !== 'Identifier' &&
                        (field.dataType === IntegrationFieldTypes.Boolean || field.dataType === IntegrationFieldTypes.String)
                )
                .map(({ name, ...field }) => ({
                    integrationType: integration?.type,
                    integrationId: integration?.id,
                    integrationName: integration?.name,
                    property: name,
                    ...field
                }))
        )
        .flat();

    return createPropertyOptions(rules, rulesToSave);
};

export const getUserRulesDictionary = (rules: AudienceRuleGraphql[]): IRulesValuesDictionary => {
    return rules.reduce((acc, item) => {
        const { integrationId } = item;
        acc[String(integrationId)] = {
            ...acc[String(integrationId)],
            [item.property]: item?.values?.map(val => ({
                label: val.name,
                value: val.name,
                amount: val.amount
            }))
        };
        return acc;
    }, {});
};

export const isActiveUser = (lastSeen: string | null | undefined, today: Date) => {
    if (lastSeen == null) {
        return false;
    }
    const offset = today.getTimezoneOffset();
    const startOfDayUtcTime = today.setHours(0, 0, 0, 0) - offset * 60 * 1000;
    const lastSeenTs = new Date(lastSeen).getTime();
    return startOfDayUtcTime - lastSeenTs < THIRTY_DAYS_AGO;
};
